import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "hiddenInput"]
  static values = {
    url: String,
    minLength: { type: Number, default: 0 }
  }

  connect() {
    this.resultsTarget.classList.add("hidden")
    // Store the original selection when the controller connects
    this.originalSelection = {
      id: this.hiddenInputTarget.value,
      text: this.inputTarget.value
    }
  }

  search() {
    const query = this.inputTarget.value.trim()
    
    // Only clear the hidden input if the visible input is completely empty
    // OR if the user has significantly changed the text from the original selection
    if (query === '') {
      this.hiddenInputTarget.value = ''
    } else if (this.hasUserChangedSelection(query)) {
      // User is typing something different from the original selection
      // Clear the hidden input so they can select a new one
      this.hiddenInputTarget.value = ''
    }
    
    // Always search, regardless of query length
    this.performSearch(query)
  }

  // Check if the user has intentionally changed the selection
  hasUserChangedSelection(currentQuery) {
    // If there was no original selection, user is free to type
    if (!this.originalSelection.text) {
      return false
    }
    
    // If the current query is completely different from the original text
    // (not just a partial match), consider it a change
    const originalText = this.originalSelection.text.toLowerCase()
    const currentText = currentQuery.toLowerCase()
    
    // If the current text doesn't start with the original text and
    // the original text doesn't start with the current text,
    // then the user is typing something completely different
    return !originalText.startsWith(currentText) && !currentText.startsWith(originalText)
  }

  performSearch(query) {
    const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
    
    // Get CSRF token
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
    
    if (token) {
      headers['X-CSRF-Token'] = token
    }
    
    fetch(url, {
      method: 'GET',
      headers: headers,
      credentials: 'same-origin'
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then(data => {
        this.displayResults(data)
      })
      .catch(error => {
        console.error('Autocomplete search error:', error)
        this.hideResults()
      })
  }

  displayResults(data) {
    this.resultsTarget.innerHTML = ""
    
    // Add a "Clear selection" option if there's a current selection
    if (this.hiddenInputTarget.value) {
      const clearDiv = document.createElement("div")
      clearDiv.className = "px-4 py-3 hover:bg-red-50 cursor-pointer transition-colors duration-150 border-b border-gray-200 text-red-600 font-medium"
      clearDiv.innerHTML = `
        <div class="flex items-center">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
          Remover seleção
        </div>
      `
      clearDiv.dataset.action = "click->autocomplete#clear"
      this.resultsTarget.appendChild(clearDiv)
    }
    
    if (data.length === 0) {
      const emptyState = document.createElement("div")
      emptyState.className = "px-4 py-3 text-sm text-gray-500 text-center italic"
      emptyState.textContent = "Nenhum resultado encontrado"
      this.resultsTarget.appendChild(emptyState)
    } else {
      data.forEach(item => {
        const div = document.createElement("div")
        div.className = "px-4 py-3 hover:bg-indigo-50 cursor-pointer transition-colors duration-150 border-b border-gray-50 last:border-b-0"
        div.textContent = item.text
        div.dataset.id = item.id
        div.dataset.action = "click->autocomplete#select"
        this.resultsTarget.appendChild(div)
      })
    }
    
    this.showResults()
  }

  showResults() {
    this.resultsTarget.classList.remove("hidden")
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
  }

  select(event) {
    const id = event.currentTarget.dataset.id
    const text = event.currentTarget.textContent
    this.hiddenInputTarget.value = id
    this.inputTarget.value = text
    this.hideResults()
    
    // Update the original selection to the new selection
    this.originalSelection = { id, text }
  }

  clear(event) {
    this.hiddenInputTarget.value = ''
    this.inputTarget.value = ''
    this.hideResults()
    
    // Update the original selection to reflect the cleared state
    this.originalSelection = { id: '', text: '' }
  }

  // Hide results when clicking outside
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  // Show results when focusing on input
  focus() {
    this.search()
  }
} 
